import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, SparklesIcon, ArrowRightIcon, MoonIcon, SunIcon, HeartIcon, LayersIcon, ActivityIcon, UsersIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tabs } from '../components/ui/Tabs';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { BetaReportPreview } from '../components/reports/BetaReportPreview';
import { PremiumReportPreview } from '../components/reports/PremiumReportPreview';
interface ReportPreviewPageProps {
  onNavigate: (page: string) => void;
  initialService?: ServiceType;
  initialReportType?: ReportType;
}
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
type ReportType = 'beta' | 'premium';
const SERVICE_OPTIONS: {
  value: ServiceType;
  label: string;
  icon: React.ReactNode;
}[] = [{
  value: 'natal',
  label: 'Натальная карта',
  icon: <MoonIcon className="w-4 h-4" data-id="element-26161" />
}, {
  value: 'solar',
  label: 'Соляр',
  icon: <SunIcon className="w-4 h-4" data-id="element-26162" />
}, {
  value: 'compatibility',
  label: 'Совместимость',
  icon: <HeartIcon className="w-4 h-4" data-id="element-26163" />
}, {
  value: 'combo',
  label: 'Комбо',
  icon: <LayersIcon className="w-4 h-4" data-id="element-26164" />
}, {
  value: 'forecast',
  label: 'Транзиты',
  icon: <ActivityIcon className="w-4 h-4" data-id="element-26165" />
}];
export function ReportPreviewPage({
  onNavigate,
  initialService,
  initialReportType
}: ReportPreviewPageProps) {
  const isDirectMode = !!initialService;
  const [selectedService, setSelectedService] = useState<ServiceType>(initialService || 'natal');
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(initialReportType || 'beta');
  const reportTabs = [{
    id: 'beta',
    label: 'Beta отчёт',
    icon: <EyeIcon className="w-4 h-4" data-id="element-26166" />
  }, {
    id: 'premium',
    label: 'Premium отчёт',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-26167" />
  }];
  const currentServiceLabel = SERVICE_OPTIONS.find(o => o.value === selectedService)?.label;
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-26168">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-8 print:hidden" data-id="element-26169">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26170">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Услуги',
          onClick: () => onNavigate('marketplace')
        }, {
          label: isDirectMode ? `Пример: ${currentServiceLabel}` : 'Примеры отчётов'
        }]} className="mb-6" data-id="element-26171" />

          <div className="text-center mb-8" data-id="element-26172">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-id="element-26173">
              {isDirectMode ? `Пример отчёта: ${currentServiceLabel}` : 'Примеры астрологических отчётов'}
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-26174">
              {isDirectMode ? `Посмотрите, как выглядит базовый (Beta) отчёт` : 'Посмотрите, как выглядят бесплатные Beta-отчёты для разных услуг платформы'}
            </p>
          </div>

          {!isDirectMode && <>
              {/* Service Selector */}
              <div className="flex flex-wrap justify-center gap-2 mb-8" data-id="element-26175">
                {SERVICE_OPTIONS.map(option => <button key={option.value} onClick={() => setSelectedService(option.value)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${selectedService === option.value ? 'bg-indigo-600 text-white shadow-md' : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200 hover:text-indigo-600'}`} data-id="element-26176">
                    {option.icon}
                    {option.label}
                  </button>)}
              </div>
            </>}
        </div>
      </div>

      {/* Report Preview */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-26177">
        <motion.div key={`${selectedService}-${selectedReportType}`} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} data-id="element-26178">
          {selectedReportType === 'beta' || !isDirectMode ? <BetaReportPreview serviceType={selectedService} onNavigate={onNavigate} data-id="element-26179" /> : <PremiumReportPreview serviceType={selectedService} data-id="element-26180" />}
        </motion.div>

        {/* CTA Section */}
        <div className="mt-12 text-center print:hidden" data-id="element-26181">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 max-w-2xl mx-auto" data-id="element-26182">
            <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-26183">
              Хотите персональный разбор от профессионала?
            </h3>
            <p className="text-slate-600 mb-6" data-id="element-26184">
              Закажите индивидуальную консультацию с опытным астрологом для
              глубокого анализа вашей карты
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-26185">
              <Button variant="outline" onClick={() => onNavigate('astrologers')} data-id="element-26186">
                <UsersIcon className="w-4 h-4 mr-2" data-id="element-26187" />
                Каталог астрологов
              </Button>
              <Button variant="primary" onClick={() => onNavigate('service-consultation')} data-id="element-26188">
                <SparklesIcon className="w-4 h-4 mr-2" data-id="element-26189" />
                Заказать консультацию
                <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-26190" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}